import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { DashboardShell } from '../components/dashboard/DashboardShell';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminActivityTab } from '../components/admin/AdminActivityTab';
import { AdminStatsTab } from '../components/admin/AdminStatsTab';
import { OverviewPage } from './dashboard/OverviewPage';
import { SubscriptionPage } from './dashboard/SubscriptionPage';
import { PaymentsPage } from './dashboard/PaymentsPage';
import { MediaLibraryPage } from './dashboard/MediaLibraryPage';
import { ComposerPage } from './dashboard/ComposerPage';
import { PlaceholderPage } from './dashboard/PlaceholderPage';
import { PageHeaderProvider } from '../context/PageHeaderContext';
import { useAuth } from '../context/AuthContext';
import { useSubscriptionData } from '../hooks/useSubscriptionData';
import {
  DEFAULT_DASHBOARD_TAB,
  getDashboardTabPath,
  isAdminTab,
  resolveDashboardTab,
} from '../constants/dashboard';
import type { DashboardTab } from '../constants/dashboard';

/**
 * Resolves the routed tab to its page.
 *
 * This file used to be 1,264 lines and contained both the dashboard chrome and
 * every tab's body. The chrome is now DashboardShell and each tab is its own
 * module; what is left is the dispatch, which is all this ever needed to be.
 */
export function DashboardPage() {
  const { isSuperAdmin } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const activeTab = resolveDashboardTab(pathname);

  const { subscription, payments, loading, error, refresh } = useSubscriptionData();

  // Set by clicking a row in the Users tab, which then jumps to Activity.
  const [adminUserFilter, setAdminUserFilter] = useState<string | undefined>(undefined);

  // Every tab gets a URL for free, so a non-admin can type /admin-users. The API
  // is the real boundary — this just sends them somewhere that renders, instead
  // of an empty page whose requests 403 behind it.
  if (isAdminTab(activeTab) && !isSuperAdmin) {
    return <Navigate to={getDashboardTabPath(DEFAULT_DASHBOARD_TAB)} replace />;
  }

  return (
    <PageHeaderProvider>
      <DashboardShell subscription={subscription}>{renderTab(activeTab)}</DashboardShell>
    </PageHeaderProvider>
  );

  function renderTab(tab: DashboardTab): React.ReactNode {
    switch (tab) {
      case 'overview':
        return <OverviewPage subscription={subscription} />;
      case 'subscription':
        return (
          <SubscriptionPage
            subscription={subscription}
            loading={loading}
            error={error}
            onCancelSuccess={refresh}
          />
        );
      case 'payments':
        return <PaymentsPage payments={payments} loading={loading} error={error} />;
      case 'admin-users':
        return (
          <AdminUsersTab
            onViewActivity={(userId) => {
              setAdminUserFilter(userId);
              navigate(getDashboardTabPath('admin-activity'));
            }}
          />
        );
      case 'admin-activity':
        return (
          <AdminActivityTab
            userIdFilter={adminUserFilter}
            onClearUserFilter={() => setAdminUserFilter(undefined)}
          />
        );
      case 'admin-stats':
        return <AdminStatsTab />;
      case 'calendar':
        return (
          <PlaceholderPage
            titleKey="placeholder.calendarTitle"
            bodyKey="placeholder.calendarBody"
          />
        );
      case 'composer':
        return <ComposerPage />;
      case 'posts':
        return (
          <PlaceholderPage titleKey="placeholder.postsTitle" bodyKey="placeholder.postsBody" />
        );
      case 'drafts':
        return (
          <PlaceholderPage titleKey="placeholder.draftsTitle" bodyKey="placeholder.draftsBody" />
        );
      case 'inbox':
        return (
          <PlaceholderPage titleKey="placeholder.inboxTitle" bodyKey="placeholder.inboxBody" />
        );
      case 'team':
        return <PlaceholderPage titleKey="placeholder.teamTitle" bodyKey="placeholder.teamBody" />;
      case 'settings':
        return (
          <PlaceholderPage
            titleKey="placeholder.settingsTitle"
            bodyKey="placeholder.settingsBody"
          />
        );
      case 'accounts':
        return (
          <PlaceholderPage
            titleKey="placeholder.accountsTitle"
            bodyKey="placeholder.accountsBody"
          />
        );
      case 'library':
        return <MediaLibraryPage />;
      case 'approvals':
        return (
          <PlaceholderPage
            titleKey="placeholder.approvalsTitle"
            bodyKey="placeholder.approvalsBody"
          />
        );
      case 'analytics':
        return (
          <PlaceholderPage
            titleKey="placeholder.analyticsTitle"
            bodyKey="placeholder.analyticsBody"
          />
        );
    }
  }
}
